import {useState} from 'react'
import {useQuery, useMutation} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Edit, Trash2, Send, CheckCircle, XCircle, Clock, FileEdit, Ban, KeyRound} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {ApprovalChain} from '@/components/ApprovalChain'
import {EscalationTimelineCard} from '@/components/shared/EscalationTimelineCard'
import {EscalationStepType} from '@/types/escalation'
import {ApprovalStepStatus} from '@/types/access-request'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {TestResultsPanel} from '@/components/restriction-rules/TestResultsPanel'
import {
    ReviewInfoCard,
    shouldShowReviewInfoCard,
} from '@/components/restriction-rules/ReviewInfoCard'
import {WorkflowTimelineCard} from '@/components/restriction-rules/WorkflowTimelineCard'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {RestrictionRuleRequestStatus, RestrictionRuleRequestType} from '@/types/restriction-rule-request'
import {RestrictionRuleStatus} from '@/types/restriction-rule'
import type {RestrictionRuleTestResult} from '@/types/restriction-rule'
import type {ApprovalStep} from '@/types/access-request'
import type {AccessProfile} from '@/types/access-profile'
import {formatTimestamp} from '@/lib/date-utils'
import {badgeWithIconClass} from '@/lib/utils'
import {PageLayout} from '@/components/shared/PageLayout'
import {IconText} from '@/components/shared/IconText'
import {MutedText} from '@/components/shared/MutedText'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {TextLink} from '@/components/shared/TextLink'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {Icon} from '@/components/shared/Icon'
import {DetailPageGrid} from '@/components/shared/DetailPageGrid'

interface ReviewRequest {
    comment?: string
}

function getRequestTypeKey(requestType: RestrictionRuleRequestType,
                           i18n: ReturnType<typeof useI18n>): string {
    switch (requestType) {
        case RestrictionRuleRequestType.CREATE: return i18n.restrictionRuleRequests.typeCreate
        case RestrictionRuleRequestType.UPDATE: return i18n.restrictionRuleRequests.typeUpdate
        case RestrictionRuleRequestType.ACTIVATE: return i18n.restrictionRuleRequests.typeActivate
        case RestrictionRuleRequestType.DEACTIVATE: return i18n.restrictionRuleRequests.typeDeactivate
        default: return requestType
    }
}

export function RestrictionRuleRequestDetail() {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {id} = useParams<{id: string}>()
    const {hasPermission} = useAuth()

    const deleteDialog = useDialog()
    const submitDialog = useDialog()
    const approveDialog = useDialog()
    const rejectDialog = useDialog()
    const cancelDialog = useDialog()
    const [reviewComment, setReviewComment] = useState('')
    const [rejectCommentError, setRejectCommentError] = useState('')

    const [testResults, setTestResults] = useState<RestrictionRuleTestResult | undefined>(undefined)
    const [testIsError, setTestIsError] = useState(false)

    const canEdit = hasPermission(permissions.restrictionRuleRequests.update)
    const canDelete = hasPermission(permissions.restrictionRuleRequests.delete)
    const canSubmit = hasPermission(permissions.restrictionRuleRequests.submit)
    const canCancel = hasPermission(permissions.restrictionRuleRequests.cancel)
    const canTest = hasPermission(permissions.restrictionRuleRequests.test)

    const {data: request, isLoading, error} = useQuery({
        queryKey: ['restriction-rule-request', id],
        queryFn: () => services.restrictionRuleRequests.detail(id!),
        enabled: !!id,
    })

    const pageTitle = request
        ? `${getRequestTypeKey(request.requestType, i18n)}: ${request.linkedRuleName ?? request.name}`
        : i18n.restrictionRuleRequests.detailPageTitle
    usePageTitle(pageTitle)

    const {data: deniedProfiles = []} = useQuery({
        queryKey: ['access-profiles', 'by-ids', request?.deniedProfileIds],
        queryFn: async () => {
            if (!request?.deniedProfileIds || request.deniedProfileIds.length === 0) return []
            const promises = request.deniedProfileIds.map(profileId =>
                services.accessProfiles.detail(profileId).catch(() => null)
            )
            const results = await Promise.all(promises)
            return results.filter((p): p is AccessProfile => p !== null)
        },
        enabled: !!request?.deniedProfileIds && request.deniedProfileIds.length > 0,
    })

    const submitMutation = useEntityMutation({
        mutationFn: () => services.restrictionRuleRequests.submit(id!),
        invalidate: [['restriction-rule-request', id], ['restriction-rule-requests']],
        successMessage: i18n.restrictionRuleRequests.requestSubmitted,
        closeDialog: submitDialog.close,
    })

    const approveMutation = useEntityMutation({
        mutationFn: (data: ReviewRequest) =>
            services.restrictionRuleRequests.approve(id!, data),
        invalidate: [['restriction-rule-request', id], ['restriction-rule-requests']],
        successMessage: i18n.restrictionRuleRequests.requestApproved,
        closeDialog: approveDialog.close,
        onSuccess: () => {
            setReviewComment('')
        },
    })

    const rejectMutation = useEntityMutation({
        mutationFn: (data: ReviewRequest) =>
            services.restrictionRuleRequests.reject(id!, data),
        invalidate: [['restriction-rule-request', id], ['restriction-rule-requests']],
        successMessage: i18n.restrictionRuleRequests.requestRejected,
        closeDialog: rejectDialog.close,
        onSuccess: () => {
            setReviewComment('')
        },
    })

    const deleteMutation = useEntityMutation({
        mutationFn: () => services.restrictionRuleRequests.remove(id!),
        invalidate: ['restriction-rule-requests'],
        successMessage: i18n.restrictionRuleRequests.requestDeleted,
        onSuccess: () => {
            navigate(routes.accessGovernance.restrictionRuleRequests.list())
        },
    })

    const cancelMutation = useEntityMutation({
        mutationFn: () => services.restrictionRuleRequests.cancel(id!),
        invalidate: [['restriction-rule-request', id], ['restriction-rule-requests']],
        successMessage: i18n.restrictionRuleRequests.requestCancelled,
        closeDialog: cancelDialog.close,
    })

    const testMutation = useMutation({
        mutationFn: () => services.restrictionRuleRequests.test(id!),
        onSuccess: (data) => {
            setTestResults(data)
            setTestIsError(false)
        },
        onError: () => {
            setTestIsError(true)
            setTestResults(undefined)
        },
    })

    const getStatusBadge = (status: RestrictionRuleRequestStatus) => {
        switch (status) {
            case RestrictionRuleRequestStatus.DRAFT:
                return {variant: BadgeVariant.SECONDARY, icon: FileEdit, label: i18n.restrictionRuleRequests.statusDraft}
            case RestrictionRuleRequestStatus.PENDING:
                return {variant: BadgeVariant.OUTLINE, icon: Clock, label: i18n.restrictionRuleRequests.statusPending}
            case RestrictionRuleRequestStatus.APPROVED:
                return {variant: BadgeVariant.SUCCESS, icon: CheckCircle, label: i18n.restrictionRuleRequests.statusApproved}
            case RestrictionRuleRequestStatus.REJECTED:
                return {variant: BadgeVariant.DESTRUCTIVE, icon: XCircle, label: i18n.restrictionRuleRequests.statusRejected}
            case RestrictionRuleRequestStatus.CANCELLED:
                return {variant: BadgeVariant.SECONDARY, icon: Ban, label: i18n.restrictionRuleRequests.statusCancelled}
            default:
                return {variant: BadgeVariant.OUTLINE, icon: FileEdit, label: status}
        }
    }

    const isEditable = request?.status === RestrictionRuleRequestStatus.DRAFT
    const isPending = request?.status === RestrictionRuleRequestStatus.PENDING

    const approvalSteps = (request?.approvalSteps ?? []) as unknown as ApprovalStep[]
    const hasApprovalChain = approvalSteps.length > 0

    const hasDecisions = approvalSteps.some(step => (step.decisions ?? []).length > 0)
    const canCancelRequest = isPending && canCancel && !hasDecisions

    const showTestPanel = canTest &&
        (request?.requestType === RestrictionRuleRequestType.CREATE ||
         request?.requestType === RestrictionRuleRequestType.UPDATE)

    const showProposedConfigCard = request?.requestType !== RestrictionRuleRequestType.ACTIVATE &&
        request?.requestType !== RestrictionRuleRequestType.DEACTIVATE

    const statusBadge = request ? getStatusBadge(request.status) : null
    const StatusIcon = statusBadge ? statusBadge.icon : null

    return (
        <PageLayout
            variant="detail"
            title={request?.name ?? i18n.restrictionRuleRequests.pageTitle}
            icon={CONCEPT_ICON.restrictionRuleRequest}
            backTo={true}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(request, 'RESTRICTION_RULE_REQUEST') : undefined)}
            data-testid="restriction-rule-request-detail-page"
            description={request && statusBadge && StatusIcon ? (
                <InlineRow>
                    <Badge variant={statusBadge.variant}
                           className={badgeWithIconClass}
                           data-testid={`status-badge-${request.status}`}>
                        <StatusIcon className="h-3 w-3"/>
                        {statusBadge.label}
                    </Badge>
                    <Badge variant="outline" data-testid="request-type-badge">
                        {getRequestTypeKey(request.requestType, i18n)}
                    </Badge>
                </InlineRow>
            ) : undefined}
            actions={request && statusBadge && StatusIcon ? (
                <InlineRow gap="sm">
                    {isEditable && canEdit && (
                        <Button
                            data-testid="btn-edit"
                            variant="outline"
                            onClick={() => navigate(routes.accessGovernance.restrictionRuleRequests.edit(id!))}
                        >
                            <IconText icon={Edit}>{i18n.common.edit}</IconText>
                        </Button>
                    )}
                    {isEditable && canSubmit && (
                        <Button
                            data-testid="btn-submit-for-approval"
                            onClick={submitDialog.open}
                        >
                            <IconText icon={Send}>{i18n.restrictionRuleRequests.submitForApproval}</IconText>
                        </Button>
                    )}
                    {canCancelRequest && (
                        <Button
                            data-testid="btn-cancel-request"
                            variant="outline"
                            onClick={cancelDialog.open}
                        >
                            <IconText icon={Ban}>{i18n.restrictionRuleRequests.cancelRequest}</IconText>
                        </Button>
                    )}
                    {isEditable && canDelete && (
                        <Button
                            data-testid="btn-delete"
                            variant="ghost"
                            size="icon"
                            onClick={deleteDialog.open}
                        >
                            <Icon icon={Trash2} className="text-destructive"/>
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {request && statusBadge && StatusIcon && <>
            <DetailPageGrid
                main={<>
                    <DetailsCard
                        data-testid="card-request-info"
                        data-help-prefix="requestInfo"
                        title={i18n.restrictionRuleRequests.requestInfoTitle}
                        description={i18n.restrictionRuleRequests.requestInfoDesc}
                        fields={[
                            ...(request.description ? [{label: i18n.common.description, value: request.description, fullWidth: true as const}] : []),
                            {
                                label: i18n.restrictionRuleRequests.requestTypeLabel,
                                value: <Badge variant="outline">{getRequestTypeKey(request.requestType, i18n)}</Badge>,
                            },
                            {
                                label: i18n.common.status,
                                value: (
                                    <Badge variant={statusBadge.variant} className={badgeWithIconClass}>
                                        <StatusIcon className="h-3 w-3"/>
                                        {statusBadge.label}
                                    </Badge>
                                ),
                            },
                            {
                                label: i18n.restrictionRuleRequests.ownerPartyLabel,
                                value: request.owners.length > 0
                                    ? (
                                        <TextLink
                                            to={routes.identityManagement.parties.detail(request.owners[0].ownerPartyId)}
                                            testId="link-owner-party"
                                            variant="bold"
                                        >
                                            {request.owners.map(o => o.ownerPartyName).join(', ')}
                                        </TextLink>
                                    )
                                    : <MutedText>—</MutedText>,
                            },
                            {label: i18n.common.createdAt, value: formatTimestamp(request.createdAt)},
                        ]}
                    />

                    {showProposedConfigCard && (
                        <Card data-testid="card-proposed-config" data-help-prefix="proposedConfig">
                            <CardHeader>
                                <CardTitle data-help-key="_section" data-testid="card-title-proposed-config">
                                    {i18n.restrictionRuleRequests.proposedConfigTitle}
                                </CardTitle>
                                <CardDescription>{i18n.restrictionRuleRequests.proposedConfigDesc}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Stack gap="md">
                                    {request.conditionExpr && (
                                        <Stack gap="xs">
                                            <MutedText size="sm" className="font-medium">{i18n.restrictionRuleRequests.conditionExprLabel}</MutedText>
                                            <CodeBlock>{request.conditionExpr}</CodeBlock>
                                        </Stack>
                                    )}

                                    {(deniedProfiles.length > 0 || request.deniedProfileCondition) && (
                                        <Stack gap="xs">
                                            <MutedText size="sm" className="font-medium">{i18n.restrictionRuleRequests.deniedProfilesLabel}</MutedText>

                                            {deniedProfiles.length > 0 ? (
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead data-testid="col-profile-name">{i18n.common.name}</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {deniedProfiles.map((profile) => (
                                                            <TableRow key={profile.id}>
                                                                <TableCell>
                                                                    <TextLink
                                                                        to={routes.accessGovernance.profiles.detail(profile.id)}
                                                                        testId={`link-profile-${profile.id}`}
                                                                        variant="bold"
                                                                    >
                                                                        {profile.name}
                                                                    </TextLink>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            ) : request.deniedProfileCondition ? (
                                                <CodeBlock>{request.deniedProfileCondition}</CodeBlock>
                                            ) : null}
                                        </Stack>
                                    )}

                                    <Stack gap="xs">
                                        <MutedText size="sm" className="font-medium">{i18n.restrictionRuleRequests.removalGraceDaysLabel}</MutedText>
                                        <p>{request.removalGraceDays} {i18n.restrictionRuleRequests.days}</p>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    <Card data-testid="card-linked-rule" data-help-prefix="linkedRule">
                        <CardHeader>
                            <CardTitle data-help-key="_section" data-testid="card-title-linked-rule">
                                {i18n.restrictionRuleRequests.linkedRuleTitle}
                            </CardTitle>
                            <CardDescription>{i18n.restrictionRuleRequests.linkedRuleDesc}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {request.ruleId ? (
                                <InlineRow className="justify-between">
                                    <TextLink
                                        to={routes.accessGovernance.restrictionRules.detail(request.ruleId)}
                                        testId="link-linked-rule"
                                        variant="bold"
                                    >
                                        {request.linkedRuleName ?? request.ruleId}
                                    </TextLink>
                                    {request.linkedRuleStatus && (
                                        <Badge variant={request.linkedRuleStatus === RestrictionRuleStatus.ACTIVE ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                            {request.linkedRuleStatus}
                                        </Badge>
                                    )}
                                </InlineRow>
                            ) : (
                                <InlineEmptyText data-testid="linked-rule-empty">
                                    {i18n.restrictionRuleRequests.standaloneNotLinkedToRule}
                                </InlineEmptyText>
                            )}
                        </CardContent>
                    </Card>

                    <Card data-testid="approval-chain-card" data-help-prefix="approvalChain">
                        <CardHeader>
                            <CardTitle data-help-key="_section" data-testid="card-title-approval-chain">
                                {i18n.approvalChain.title}
                            </CardTitle>
                            {request.matchedPolicyName && (
                                <CardDescription>
                                    {i18n.restrictionRuleRequests.matchedPolicy({name: request.matchedPolicyName})}
                                </CardDescription>
                            )}
                        </CardHeader>
                        <CardContent>
                            {hasApprovalChain ? (
                                <ApprovalChain
                                    steps={approvalSteps}
                                    onApprove={approveDialog.open}
                                    onReject={rejectDialog.open}
                                    itemId={request.id}
                                    requiredPermission={permissions.restrictionRuleRequests.approve}
                                />
                            ) : (
                                <InlineEmptyText data-testid="approval-chain-empty">
                                    {i18n.restrictionRuleRequests.noApproversConfigured}
                                </InlineEmptyText>
                            )}
                        </CardContent>
                    </Card>
                    {approvalSteps
                        .filter(step => step.status === ApprovalStepStatus.ACTIVE)
                        .map(step => (
                            <EscalationTimelineCard
                                key={`escalation-${step.id}`}
                                stepId={step.id}
                                stepType={EscalationStepType.RESTRICTION_RULE_REQUEST}
                                escalationLevel={step.escalationLevel}
                                data-testid={`escalation-timeline-${step.id}`}
                            />
                        ))}

                    {showTestPanel && (
                        <Card data-testid="card-test-panel" data-help-prefix="testPanel">
                            <CardHeader>
                                <CardTitle data-help-key="_section" data-testid="card-title-test-panel">
                                    {i18n.restrictionRuleRequests.testPanelTitle}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Stack gap="md">
                                    <Button
                                        variant="outline"
                                        onClick={() => testMutation.mutate()}
                                        disabled={testMutation.isPending}
                                        data-testid="btn-run-test"
                                    >
                                        <IconText icon={KeyRound}>
                                            {testMutation.isPending
                                                ? i18n.restrictionRuleRequests.testing
                                                : i18n.restrictionRuleRequests.runTest}
                                        </IconText>
                                    </Button>
                                    {(testResults !== undefined || testMutation.isPending || testIsError) && (
                                        <TestResultsPanel
                                            result={testResults}
                                            isLoading={testMutation.isPending}
                                            isError={testIsError}
                                        />
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}
                </>}
                sidebar={
                    <Stack gap="lg">
                        {shouldShowReviewInfoCard(request) && <ReviewInfoCard request={request}/>}
                        <WorkflowTimelineCard request={request}/>
                    </Stack>
                }
            />

            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.restrictionRuleRequests.deleteDialogTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.restrictionRuleRequests.deleteDialogDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete-footer"
                        onCancel={deleteDialog.close}
                        onSave={() => deleteMutation.mutate()}
                        saveType="button"
                        saving={deleteMutation.isPending}
                        destructive
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={submitDialog.isOpen} onOpenChange={submitDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.restrictionRuleRequests.submitDialogTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.restrictionRuleRequests.submitDialogDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="submit-footer"
                        onCancel={submitDialog.close}
                        onSave={() => submitMutation.mutate()}
                        saveType="button"
                        saving={submitMutation.isPending}
                        saveLabel={i18n.common.submit}
                        savingLabel={i18n.common.submitting}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={approveDialog.isOpen} onOpenChange={approveDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.restrictionRuleRequests.approveDialogTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.restrictionRuleRequests.approveDialogDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault()
                        approveMutation.mutate({comment: reviewComment || undefined})
                    }}>
                        <Stack gap="sm" className="py-4">
                            <Label htmlFor="approve-comment">{i18n.restrictionRuleRequests.commentOptional}</Label>
                            <Textarea
                                id="approve-comment"
                                placeholder={i18n.restrictionRuleRequests.commentPlaceholder}
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                data-testid="textarea-approve-comment"
                            />
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="approve-footer"
                            onCancel={() => {
                                approveDialog.close()
                                setReviewComment('')
                            }}
                            saving={approveMutation.isPending}
                            saveLabel={i18n.common.approve}
                            savingLabel={i18n.restrictionRuleRequests.approving}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            <Dialog open={rejectDialog.isOpen} onOpenChange={rejectDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.restrictionRuleRequests.rejectDialogTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.restrictionRuleRequests.rejectDialogDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault()
                        if (!reviewComment.trim()) {
                            setRejectCommentError(i18n.restrictionRuleRequests.rejectCommentRequired)
                            return
                        }
                        setRejectCommentError('')
                        rejectMutation.mutate({comment: reviewComment})
                    }}>
                        <Stack gap="sm" className="py-4">
                            <Label htmlFor="reject-comment">{i18n.restrictionRuleRequests.rejectionReason} *</Label>
                            <Textarea
                                id="reject-comment"
                                placeholder={i18n.restrictionRuleRequests.rejectionReasonPlaceholder}
                                value={reviewComment}
                                onChange={(e) => {
                                    setReviewComment(e.target.value)
                                    if (e.target.value.trim()) setRejectCommentError('')
                                }}
                                data-testid="textarea-reject-comment"
                            />
                            {rejectCommentError && (
                                <MutedText size="sm" className="text-destructive mt-1" data-testid="error-reject-comment">
                                    {rejectCommentError}
                                </MutedText>
                            )}
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="reject-footer"
                            onCancel={() => {
                                rejectDialog.close()
                                setReviewComment('')
                                setRejectCommentError('')
                            }}
                            saving={rejectMutation.isPending}
                            destructive
                            saveLabel={i18n.common.reject}
                            savingLabel={i18n.restrictionRuleRequests.rejecting}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            <Dialog open={cancelDialog.isOpen} onOpenChange={cancelDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.restrictionRuleRequests.cancelDialogTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.restrictionRuleRequests.cancelDialogDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="cancel-footer"
                        onCancel={cancelDialog.close}
                        onSave={() => cancelMutation.mutate()}
                        saveType="button"
                        saving={cancelMutation.isPending}
                        destructive
                        saveLabel={i18n.restrictionRuleRequests.withdrawRequest}
                        savingLabel={i18n.restrictionRuleRequests.withdrawing}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
